import React from 'react';
import type {
  TimeRemaining,
  Alignment,
  Size,
  Layout,
  DisplayFormat,
  NumberStyle,
  SeparatorStyle,
} from './types';
import {
  formatNumber,
  getSeparator,
  getNumberStyleClasses,
  FLEX_ALIGNMENT_CLASSES,
  NUMBER_SIZE_CLASSES,
} from './utils';

interface CountdownDisplayProps {
  timeRemaining: TimeRemaining;
  units: { showDays: boolean; showHours: boolean; showMinutes: boolean; showSeconds: boolean };
  alignment: Alignment;
  size: Size;
  layout: Layout;
  displayFormat: DisplayFormat;
  numberStyle: NumberStyle;
  separatorStyle: SeparatorStyle;
  showLabels: boolean;
  numberColor: string;
  labelColor: string;
  labelFontStyle?: React.CSSProperties;
}

interface UnitDisplayProps {
  value: number;
  label: string;
  showLabel: boolean;
  displayFormat: DisplayFormat;
  numberStyle: NumberStyle;
  size: Size;
  numberColor: string;
  labelColor: string;
  labelFontStyle?: React.CSSProperties;
}

const UnitDisplay: React.FC<UnitDisplayProps> = ({
  value,
  label,
  showLabel,
  displayFormat,
  numberStyle,
  size,
  numberColor,
  labelColor,
  labelFontStyle = {},
}) => {
  const labelSizeClass = displayFormat === 'compact' ? 'text-xs' : 'text-sm md:text-base';
  const isCompact = displayFormat === 'compact';

  const numberColorStyle =
    numberStyle === 'gradient' ? undefined : numberStyle === 'card' ? '#000000' : numberColor;

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-baseline gap-1">
        <div
          className={`${getNumberStyleClasses(numberStyle)} ${NUMBER_SIZE_CLASSES[size]}`}
          style={{ color: numberColorStyle }}
        >
          {formatNumber(value)}
        </div>
        {showLabel && isCompact && (
          <span
            className={`${labelSizeClass} font-medium`}
            style={{ color: labelColor, ...labelFontStyle }}
          >
            {label}
          </span>
        )}
      </div>
      {showLabel && displayFormat === 'full' && (
        <div className={`${labelSizeClass} mt-2`} style={{ color: labelColor, ...labelFontStyle }}>
          {label}
        </div>
      )}
    </div>
  );
};

export const CountdownDisplay: React.FC<CountdownDisplayProps> = ({
  timeRemaining,
  units,
  alignment,
  size,
  layout,
  displayFormat,
  numberStyle,
  separatorStyle,
  showLabels,
  numberColor,
  labelColor,
  labelFontStyle = {},
}) => {
  const unitValues: number[] = [];
  const fullLabels: string[] = [];
  const abbreviatedLabels: string[] = [];

  if (units.showDays) {
    unitValues.push(timeRemaining.days);
    fullLabels.push('Days');
    abbreviatedLabels.push('d');
  }
  if (units.showHours) {
    unitValues.push(timeRemaining.hours);
    fullLabels.push('Hours');
    abbreviatedLabels.push('h');
  }
  if (units.showMinutes) {
    unitValues.push(timeRemaining.minutes);
    fullLabels.push('Minutes');
    abbreviatedLabels.push('m');
  }
  if (units.showSeconds) {
    unitValues.push(timeRemaining.seconds);
    fullLabels.push('Seconds');
    abbreviatedLabels.push('s');
  }

  const getLabelText = (index: number): string => {
    if (displayFormat === 'minimal') return '';
    if (displayFormat === 'compact') return abbreviatedLabels[index];
    return fullLabels[index];
  };

  const shouldShowLabels = showLabels && displayFormat !== 'minimal';
  const separator = getSeparator(separatorStyle);

  const getGapClass = () => {
    if (displayFormat === 'minimal') return 'gap-2';
    if (displayFormat === 'compact') return 'gap-3';
    return 'gap-4';
  };

  // Vertical layout
  if (layout === 'vertical') {
    return (
      <div className={`flex flex-col ${getGapClass()} ${FLEX_ALIGNMENT_CLASSES[alignment]}`}>
        {unitValues.map((value, index) => (
          <UnitDisplay
            key={index}
            value={value}
            label={getLabelText(index)}
            showLabel={shouldShowLabels}
            displayFormat={displayFormat}
            numberStyle={numberStyle}
            size={size}
            numberColor={numberColor}
            labelColor={labelColor}
            labelFontStyle={labelFontStyle}
          />
        ))}
      </div>
    );
  }

  // Grid layout
  if (layout === 'grid') {
    const gridCols =
      unitValues.length === 4
        ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
        : unitValues.length === 3
          ? 'grid-cols-1 sm:grid-cols-3'
          : unitValues.length === 2
            ? 'grid-cols-1 sm:grid-cols-2'
            : 'grid-cols-1';

    const gridGap =
      displayFormat === 'minimal'
        ? 'gap-2 md:gap-3'
        : displayFormat === 'compact'
          ? 'gap-3 md:gap-4'
          : 'gap-4 md:gap-6';

    return (
      <div className={`grid ${gridCols} ${gridGap}`}>
        {unitValues.map((value, index) => (
          <UnitDisplay
            key={index}
            value={value}
            label={getLabelText(index)}
            showLabel={shouldShowLabels}
            displayFormat={displayFormat}
            numberStyle={numberStyle}
            size={size}
            numberColor={numberColor}
            labelColor={labelColor}
            labelFontStyle={labelFontStyle}
          />
        ))}
      </div>
    );
  }

  // Horizontal layout (default)
  const horizontalGap =
    displayFormat === 'minimal'
      ? 'gap-1 md:gap-2'
      : displayFormat === 'compact'
        ? 'gap-2 md:gap-3'
        : 'gap-2 md:gap-4';

  return (
    <div
      className={`flex flex-wrap items-start ${horizontalGap} ${FLEX_ALIGNMENT_CLASSES[alignment]}`}
    >
      {unitValues.map((value, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center">
            <div className="flex items-baseline gap-1">
              <div
                className={`${getNumberStyleClasses(numberStyle)} ${NUMBER_SIZE_CLASSES[size]}`}
                style={{
                  color:
                    numberStyle === 'gradient'
                      ? undefined
                      : numberStyle === 'card'
                        ? '#000000'
                        : numberColor,
                }}
              >
                {formatNumber(value)}
              </div>
              {shouldShowLabels && displayFormat === 'compact' && (
                <span
                  className="text-xs font-medium"
                  style={{ color: labelColor, ...labelFontStyle }}
                >
                  {getLabelText(index)}
                </span>
              )}
            </div>
            {shouldShowLabels && displayFormat === 'full' && (
              <div
                className="text-xs md:text-sm mt-1"
                style={{ color: labelColor, ...labelFontStyle }}
              >
                {getLabelText(index)}
              </div>
            )}
          </div>
          {index < unitValues.length - 1 && separator && displayFormat !== 'minimal' && (
            <div
              className={`font-bold leading-none ${
                displayFormat === 'compact' ? 'text-lg md:text-xl' : NUMBER_SIZE_CLASSES[size]
              }`}
              style={{ color: numberColor }}
            >
              {separator}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
